import { useState } from "react";
import { Ellipsis, SquarePen, Trash } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "components/ui/dropdown-menu";
import { CustomDialog } from "components/blocks/custom-dialog";
import { DebouncedInput } from "components/blocks/debounced-input/debounced-input";
import { useToast } from "../../../hooks/use-toast";
import { useConfig } from "../../../hooks/use-config";
import { useChatStore } from "../../../state/store/chat/use-chat-store";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useRenameProject } from "../hooks/use-create-project";
import { deleteProjectById } from "../services/projects-services";
import { useQueryClient } from "@tanstack/react-query";

export interface ProjectActionsProps {
    projectId: string;
    projectName: string;
}

export const ProjectActions = ({ projectId, projectName }: ProjectActionsProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { configurationSettings } = useConfig();
    const { mutateAsync: renameProject } = useRenameProject();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { projectId: currentProjectId, conversationId } = useParams();

    const updateProjectNameInStore = useChatStore((s) => s.updateProjectNameInStore);
    const setSidebarProjectsList = useChatStore((s) => s.setSidebarProjectsList);
    const sidebarProjectsList = useChatStore((s) => s.sidebarProjectsList);

    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRenameLoading, setIsRenameLoading] = useState(false);
    const [newTitle, setNewTitle] = useState(projectName || "");
    const [actionType, setActionType] = useState<"delete" | "rename" | null>(null);

    const handleRename = async () => {
        if (!newTitle.trim()) return;
        try {
            setIsRenameLoading(true);
            if (projectId) await renameProject({ payload: { projectId, name: newTitle } })
            updateProjectNameInStore(projectId, newTitle);
            queryClient.invalidateQueries({ queryKey: ["getProject", projectId] });
            toast({ variant: "success", title: t("SUCCESS"), description: t("PROJECT_RENAME_SUCCESS_DESCRIPTION") });
        } catch {
            toast({ variant: "destructive", title: t("RENAME_FAILED"), description: t("PROJECT_RENAME_FAILED_DESCRIPTION") });
        } finally {
            setIsRenameDialogOpen(false);
            setIsRenameLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setIsLoading(true);
            await deleteProjectById(configurationSettings, projectId);
            setSidebarProjectsList(sidebarProjectsList.filter((project) => project.id !== projectId));
            const project = sidebarProjectsList.find((project) => project.id === projectId);
            if (conversationId && project?.items.some((item) => item.ConversationId === conversationId)) {
                useChatStore.getState().setNewChat(true);
                navigate("/chat");
            }
            if (projectId && projectId === currentProjectId) {
                useChatStore.getState().setNewChat(true);
                navigate("/chat");
            }
            toast({ variant: "success", title: t("SUCCESS"), description: t("PROJECT_DELETE_SUCCESS_DESCRIPTION") });
        } catch {
            toast({ variant: "destructive", title: t("DELETE_FAILED"), description: t("PROJECT_DELETE_FAILED_DESCRIPTION") });
        } finally {
            setIsConfirmDialogOpen(false);
            setIsLoading(false);
        }
    };

    const handleConfirmAction = () => {
        if (actionType === "rename") handleRename();
        if (actionType === "delete") handleDelete();
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="ml-1 p-1 rounded-md hover:bg-muted-foreground/10">
                        <Ellipsis size={16} strokeWidth={2.2} className="text-primary" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="end" className="min-w-48 rounded-[8px]">
                    <DropdownMenuItem onClick={() => { setIsRenameDialogOpen(true); setNewTitle(projectName); setActionType("rename"); }}>
                        <SquarePen className="text-primary" size={16} strokeWidth={2.2} />
                        <span className="text-sm font-light">{t("RENAME")}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="flex items-center gap-2 text-destructive focus:text-destructive" onClick={() => { setIsConfirmDialogOpen(true); setActionType("delete"); }}>
                        <Trash className="text-destructive" size={16} strokeWidth={2.2} />
                        <span className="text-sm font-light">{t("DELETE")}</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <CustomDialog
                isOpen={isConfirmDialogOpen}
                isLoading={isLoading}
                setIsOpen={setIsConfirmDialogOpen}
                title={t("CONFIRM_ACTION")}
                onConfirm={handleConfirmAction}
                confirmText={t("YES")}
                cancelText={t("CANCEL")}
                description={""}
            >
                <p>{t("CONFIRM_ACTION_DESCRIPTION")}</p>
            </CustomDialog>

            <CustomDialog
                isOpen={isRenameDialogOpen}
                isLoading={isRenameLoading}
                setIsOpen={setIsRenameDialogOpen}
                title={t("RENAME")}
                onConfirm={handleConfirmAction}
                confirmText={t("SAVE")}
                cancelText={t("CANCEL")}
                isConfirmDisabled={newTitle.trim() === ""}
                description={""}
            >
                <DebouncedInput
                    placeholder={t("ENTER_NEW_TITLE")}
                    initialValue={newTitle}
                    onDebouncedChange={setNewTitle}
                    className="
                                        h-10 w-full rounded-3xl
                                        border border-primary
                                        bg-background pr-8
                                        focus:outline-none
                                        focus:border-primary
                                        focus:ring-1 focus:ring-primary
                                    "
                />
            </CustomDialog>
        </>
    );
};